import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatDateIST } from "@/lib/formatUtils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import logger from "@/lib/logger";

const MASTER_TYPES = [
  { key: "parties", label: "Party Master" },
  { key: "employees", label: "Employee Master" },
  { key: "vehicles", label: "Vehicle Master" },
  { key: "keys", label: "Key Master" },
  { key: "divisions", label: "Divisions / Sections" },
  { key: "process_types", label: "Process Types" },
  { key: "courier_companies", label: "Courier Companies" },
  { key: "vehicle_types", label: "Vehicle Types" },
  { key: "purposes", label: "Purpose of Visit" },
];

export default function MasterDataTab() {
  const [masterType, setMasterType] = useState("parties");
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("Company");
  const [newVehicleType, setNewVehicleType] = useState("");
  const [newEmployeeName, setNewEmployeeName] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isVehicleMaster = masterType === "vehicles";

  const fetchMasters = useCallback(async () => {
    try {
      const { data } = await api.get(`/masters/${masterType}`);
      setItems(data.items || []);
    } catch (err) { logger.error("Failed to fetch masters:", err); }
  }, [masterType]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);

  const handleAddMaster = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const payload = { name: newName.trim() };
      if (isVehicleMaster) {
        payload.category = newCategory;
        payload.details = { vehicle_type: newVehicleType.trim(), employee_name: newCategory === "Employee" ? newEmployeeName.trim() : "" };
      }
      await api.post(`/masters/${masterType}`, payload);
      toast.success("Added");
      setNewName(""); setNewCategory("Company"); setNewVehicleType(""); setNewEmployeeName("");
      setShowAdd(false);
      fetchMasters();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/masters/${masterType}/${id}`);
      toast.success("Deleted");
      fetchMasters();
    } catch (err) { logger.error("Operation failed:", err); toast.error("Failed"); }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mt-4">
        <Card className="p-3 col-span-1">
          <h3 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3">Master Lists</h3>
          <div className="space-y-0.5 grid grid-cols-2 lg:grid-cols-1 gap-0.5">
            {MASTER_TYPES.map(m => (
              <button
                key={m.key}
                onClick={() => setMasterType(m.key)}
                data-testid={`master-${m.key}`}
                className={`w-full text-left px-3 py-2.5 rounded text-sm transition ${
                  masterType === m.key ? "bg-[#1B3C87] text-white font-semibold" : "hover:bg-gray-50"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </Card>
        <Card className="col-span-1 lg:col-span-3 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>
              {MASTER_TYPES.find(m => m.key === masterType)?.label}
            </h3>
            <Button onClick={() => setShowAdd(true)} className="bg-[#1B3C87] hover:bg-[#2650A8] h-9 text-xs" data-testid="add-master-btn">
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#1B3C87]/5">
                <TableHead className="text-xs uppercase font-bold">Name</TableHead>
                {isVehicleMaster && <TableHead className="text-xs uppercase font-bold">Type</TableHead>}
                {isVehicleMaster && <TableHead className="text-xs uppercase font-bold">Owner</TableHead>}
                <TableHead className="text-xs uppercase font-bold">Status</TableHead>
                <TableHead className="text-xs uppercase font-bold">Created</TableHead>
                <TableHead className="text-xs uppercase font-bold w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={isVehicleMaster ? 6 : 4} className="text-center py-6 text-gray-400">No items</TableCell></TableRow>}
              {items.map(item => (
                <TableRow key={item.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{item.name}</TableCell>
                  {isVehicleMaster && <TableCell className="text-sm">{item.details?.vehicle_type || "\u2014"}</TableCell>}
                  {isVehicleMaster && (
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.category === "Employee" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                        {item.category || "Company"}{item.category === "Employee" && item.details?.employee_name ? ` — ${item.details.employee_name}` : ""}
                      </span>
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant={item.is_active !== false ? "default" : "secondary"} className={item.is_active !== false ? "bg-emerald-600" : ""}>
                      {item.is_active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{item.created_at ? formatDateIST(item.created_at) : "—"}</TableCell>
                  <TableCell>
                    {user?.role === "admin" && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-2 text-xs text-gray-400">{items.length} items</div>
        </Card>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add {MASTER_TYPES.find(m => m.key === masterType)?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">{isVehicleMaster ? "Vehicle No." : "Name"}</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} className="h-11" placeholder={isVehicleMaster ? "e.g. PB10AB1234" : ""} data-testid="new-master-name" />
            </div>
            {isVehicleMaster && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle Type / Model</Label>
                  <Input value={newVehicleType} onChange={e => setNewVehicleType(e.target.value)} className="h-11" placeholder="e.g. Innova, Bolero, Swift" data-testid="new-vehicle-type" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Owner Type</Label>
                  <Select value={newCategory} onValueChange={setNewCategory}>
                    <SelectTrigger className="h-11" data-testid="new-vehicle-owner">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Company">Company Vehicle</SelectItem>
                      <SelectItem value="Employee">Employee Vehicle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newCategory === "Employee" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Employee Name</Label>
                    <Input value={newEmployeeName} onChange={e => setNewEmployeeName(e.target.value)} className="h-11" placeholder="Name of vehicle owner" data-testid="new-vehicle-employee" />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAddMaster} disabled={saving} className="bg-[#1B3C87]" data-testid="save-master-btn">
              {saving ? "Saving..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
